import React, { useState, useEffect } from 'react';
import { X, Download, CheckSquare, Square, FolderOpen } from 'lucide-react';
import { Notebook, Language } from '../types';

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    notebooks: Notebook[];
    onExport: (selectedNotebooks: Notebook[]) => void;
    language: Language;
}

export const ExportModal: React.FC<ExportModalProps> = ({
    isOpen,
    onClose,
    notebooks,
    onExport,
    language
}) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Auto-select all notebooks when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedIds(new Set(notebooks.map(nb => nb.id)));
        }
    }, [isOpen, notebooks]);

    if (!isOpen) return null;

    const toggleSelection = (id: string) => {
        const next = new Set(selectedIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedIds(next);
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === notebooks.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(notebooks.map(nb => nb.id)));
        }
    };

    const handleExport = () => {
        const selectedNotebooks = notebooks.filter(nb => selectedIds.has(nb.id));
        onExport(selectedNotebooks);
        onClose();
    };

    const isAllSelected = notebooks.length > 0 && selectedIds.size === notebooks.length;
    const isNoneSelected = selectedIds.size === 0;

    const t = {
        english: {
            title: "Export Backup",
            description: "Select the notebooks you want to include in the backup file.",
            selectAll: "Select All",
            deselectAll: "Deselect All",
            cancel: "Cancel",
            exportBtn: `Export ${selectedIds.size} Notebook${selectedIds.size !== 1 ? 's' : ''}`,
            emptyMessage: "No notebooks available to export."
        },
        vietnamese: {
            title: "Xuất dữ liệu",
            description: "Chọn các sổ tay bạn muốn lưu trữ vào file backup.",
            selectAll: "Chọn tất cả",
            deselectAll: "Bỏ chọn tất cả",
            cancel: "Hủy",
            exportBtn: `Xuất ${selectedIds.size} Sổ tay`,
            emptyMessage: "Không có sổ tay nào để xuất."
        }
    };

    const labels = t[language] || t.english;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10005] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl border dark:border-slate-700 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                
                {/* Header */}
                <div className="p-5 border-b dark:border-slate-700 flex justify-between items-center bg-gray-50 dark:bg-slate-900 shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-lg">
                            <Download size={20} />
                        </div>
                        <h3 className="font-bold text-lg dark:text-white">{labels.title}</h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full transition-colors text-gray-500">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col flex-1 min-h-0">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 shrink-0">
                        {labels.description}
                    </p>

                    {notebooks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                            <FolderOpen size={48} className="mb-2 opacity-50" />
                            <p>{labels.emptyMessage}</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-between items-center mb-3 shrink-0">
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {selectedIds.size} / {notebooks.length}
                                </span>
                                <button
                                    onClick={toggleSelectAll}
                                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium flex items-center gap-1.5"
                                >
                                    {isAllSelected ? <Square size={16} /> : <CheckSquare size={16} />}
                                    {isAllSelected ? labels.deselectAll : labels.selectAll}
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto border dark:border-slate-700 rounded-xl bg-gray-50 dark:bg-slate-900">
                                <div className="divide-y dark:divide-slate-700">
                                    {notebooks.map(nb => {
                                        const isSelected = selectedIds.has(nb.id);
                                        return (
                                            <div
                                                key={nb.id}
                                                onClick={() => toggleSelection(nb.id)}
                                                className={`flex items-center p-3 cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-slate-800/50 ${isSelected ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}
                                            >
                                                <div className={`flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center mr-3 transition-colors ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 dark:border-slate-600'}`}>
                                                    {isSelected && <CheckSquare size={14} className="opacity-100" />}
                                                </div>
                                                <div className="flex items-center gap-2 overflow-hidden flex-1">
                                                    <FolderOpen size={16} className={isSelected ? 'text-indigo-500' : 'text-gray-400'} />
                                                    <span className={`text-sm truncate ${isSelected ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                                                        {nb.title}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 whitespace-nowrap">
                                                    {nb.nodes.length} nodes
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-5 border-t dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex justify-end gap-3 shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        {labels.cancel}
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={isNoneSelected}
                        className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg transition-colors shadow-lg shadow-indigo-500/30 flex items-center justify-center gap-2"
                    >
                        <Download size={16} />
                        {labels.exportBtn}
                    </button>
                </div>

            </div>
        </div>
    );
};
